import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Phone, Mail, MapPin, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const heroRef = useRef(null);
  const formRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const formInView = useInView(formRef, { once: true, margin: "-100px" });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/clients/submit`, formData);
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Website Design & Development",
    "AI-Powered Business Websites",
    "Restaurant & Business Systems",
    "Custom Digital Solutions",
    "Other"
  ];

  return (
    <div data-testid="contact-page" className="relative">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[40vh] flex items-center pt-32 pb-16 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-mesh" />

        {/* Decorative */}
        <div className="absolute top-1/3 right-0 w-80 h-80 bg-gold/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-violet-glow/10 rounded-full blur-[100px]" />

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <motion.span
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block"
          >
            Get In Touch
          </motion.span>

          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-none mb-6"
          >
            <span className="text-white">Let's Create</span>
            <br />
            <span className="text-gradient-gold">Together</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-white/60 text-base md:text-lg max-w-xl leading-relaxed"
          >
            Ready to transform your vision into reality? We'd love to hear about
            your project and discuss how we can help you achieve your goals.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section
        ref={formRef}
        className="relative py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={formInView ? "visible" : "hidden"}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12"
          >
            {/* Contact Info */}
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="lg:col-span-4 space-y-8"
            >
              {/* Contact Cards */}
              <div className="space-y-4">
                <a
                  href="tel:+27699512915"
                  data-testid="contact-phone-primary"
                  className="flex items-center gap-4 p-5 glass-card rounded-2xl hover:border-gold/30 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <Phone size={22} strokeWidth={1.5} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Primary</p>
                    <p className="text-white font-medium">+27 699 512 915</p>
                  </div>
                </a>

                <a
                  href="tel:0768334742"
                  data-testid="contact-phone-secondary"
                  className="flex items-center gap-4 p-5 glass-card rounded-2xl hover:border-gold/30 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <Phone size={22} strokeWidth={1.5} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Secondary</p>
                    <p className="text-white font-medium">0768 334 742</p>
                  </div>
                </a>

                <a
                  href="mailto:reaganmbao5@gmail.com"
                  data-testid="contact-email"
                  className="flex items-center gap-4 p-5 glass-card rounded-2xl hover:border-gold/30 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <Mail size={22} strokeWidth={1.5} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Email</p>
                    <p className="text-white font-medium">reaganmbao5@gmail.com</p>
                  </div>
                </a>
              </div>

              {/* Social Links */}
              <div className="pt-8 border-t border-white/10">
                <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Connect With Us</p>
                <div className="flex gap-3">
                  <a
                    href="https://wa.me/message/W74V54QZ3UFWO1?src=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp"
                    className="w-12 h-12 rounded-xl glass flex items-center justify-center text-white/50 hover:text-gold hover:border-gold/50 transition-all duration-300"
                    title="WhatsApp"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/sr.jaeyvee?igsh=MWYzZWE0c2w0dWh4&utm_source=ig_contact_invite"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="w-12 h-12 rounded-xl glass flex items-center justify-center text-white/50 hover:text-gold hover:border-gold/50 transition-all duration-300"
                    title="Instagram"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/jaey.vee.31/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-facebook"
                    className="w-12 h-12 rounded-xl glass flex items-center justify-center text-white/50 hover:text-gold hover:border-gold/50 transition-all duration-300"
                    title="Facebook"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* QR Code */}
              <div className="pt-8 border-t border-white/10">
                <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Scan to Call</p>
                <div className="glass-card p-4 rounded-2xl inline-block hover:border-gold/30 transition-colors duration-300">
                  <div className="bg-white p-2 rounded-xl">
                    <img
                      src="/assets/call-qr.png"
                      alt="Scan to Call +27 699 512 915"
                      className="w-32 h-32 object-contain"
                    />
                  </div>
                  <p className="text-gold/80 text-[10px] text-center mt-3 font-mono uppercase tracking-widest">
                    +27 699 512 915
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="lg:col-span-8"
            >
              <div className="glass-card rounded-3xl p-8 md:p-10">
                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="contact-success"
                  >
                    <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={40} className="text-gold" />
                    </div>
                    <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
                      Request Received!
                    </h3>
                    <p className="text-white/60 max-w-md mx-auto mb-8">
                      Thank you for reaching out. We'll review your request and
                      get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setIsSuccess(false)}
                      className="text-gold hover:text-gold-light transition-colors font-mono text-sm uppercase tracking-widest"
                    >
                      Submit Another Request
                    </button>
                  </motion.div>
                ) : (
                  <>
                    <div className="mb-8">
                      <h2 className="font-heading text-2xl md:text-3xl text-white mb-2">
                        Request a Consultation
                      </h2>
                      <p className="text-white/50">
                        Interested in working together? Tell me about your project.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                            Your Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            data-testid="contact-input-name"
                            className="input-underline"
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            data-testid="contact-input-email"
                            className="input-underline"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            data-testid="contact-input-phone"
                            className="input-underline"
                            placeholder="+260 XXX XXX XXX"
                          />
                        </div>
                        <div>
                          <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                            Service Interest
                          </label>
                          <select
                            name="service_type"
                            value={formData.service_type}
                            onChange={handleChange}
                            data-testid="contact-input-service"
                            className="input-underline bg-transparent cursor-pointer"
                          >
                            <option value="" className="bg-background-paper">Select a service</option>
                            {services.map((service) => (
                              <option key={service} value={service} className="bg-background-paper">
                                {service}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                          Project Details *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={4}
                          data-testid="contact-input-message"
                          className="input-underline resize-none"
                          placeholder="Tell us about your project, goals, and timeline..."
                        />
                      </div>

                      {error && (
                        <div data-testid="contact-error" className="text-red-400 text-sm">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        data-testid="contact-submit-button"
                        className="glass-button px-10 py-5 rounded-full font-mono text-sm uppercase tracking-widest text-gold flex items-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Request
                            <Send size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
